import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navigation from "../components/Navigation";
import BulkMessageComposer from "../components/BulkMessageComposer";
import ConsultantRequestButton from "../components/ConsultantRequestButton";
import MessageAttachmentFavoriteButton from "../components/MessageAttachmentFavoriteButton";
import LinkifyText from "../components/LinkifyText";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { FileText, TrendingUp, Target, MessageSquare, Users, Send, Search, User, Paperclip, Image, Video, File, X, Camera, Upload, School, Mail } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

import { menuItems } from "../config/menuItems";

export default function Messages({ user, onLogout }) {
  const [messages, setMessages] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [allUsers, setAllUsers] = useState([]);
  const [showUserSearch, setShowUserSearch] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [showMediaMenu, setShowMediaMenu] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isSendingSchoolMsg, setIsSendingSchoolMsg] = useState(false);
  const [coachRequests, setCoachRequests] = useState([]);
  const [activeTab, setActiveTab] = useState("direct"); // direct, team, school
  const [userTeams, setUserTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamMessages, setTeamMessages] = useState([]);
  const [schoolMessages, setSchoolMessages] = useState([]);
  const [showSchoolMessageForm, setShowSchoolMessageForm] = useState(false);
  const [schoolMessageData, setSchoolMessageData] = useState({
    title: "",
    content: "",
    priority: "normal"
  });
  const [showBulkComposer, setShowBulkComposer] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchConversations();
    fetchAllUsers();
    
    // Set up polling for new messages
    const interval = setInterval(fetchConversations, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.other_user_id);
    }
  }, [selectedConversation]);

  const fetchConversations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/messages/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data);
    } catch (error) {
      console.error("Failed to fetch conversations:", error);
    }
  };

  const fetchMessages = async (otherUserId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/direct-messages/${otherUserId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      toast.error("Failed to load messages");
    }
  };

  const fetchAllUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllUsers(response.data.filter(u => u.id !== user.id));
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/messages`, {
        recipient_id: selectedConversation.other_user_id,
        content: newMessage.trim(),
        message_type: "direct"
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setNewMessage("");
      fetchMessages(selectedConversation.other_user_id);
      fetchConversations();
    } catch (error) {
      toast.error("Failed to send message");
    }
  };

  const startNewConversation = (otherUser) => {
    setSelectedConversation({
      other_user_id: otherUser.id,
      other_user_name: otherUser.name,
      other_user_role: otherUser.role,
      last_message_time: new Date().toISOString()
    });
    setShowUserSearch(false);
    setSearchTerm("");
  };

  const filteredUsers = allUsers.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }

    // Check file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'video/mp4', 'video/quicktime', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("File type not supported. Please use images, videos, or PDFs.");
      return;
    }

    setSelectedFile(file);

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setFilePreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }

    setShowMediaMenu(false);
  };

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      setIsUploading(true);
      setUploadProgress(0);

      const token = localStorage.getItem('token');
      
      // Simulated chunked upload with progress
      const chunkSize = 1024 * 1024; // 1MB chunks
      const totalChunks = Math.ceil(file.size / chunkSize);
      
      for (let i = 0; i < totalChunks; i++) {
        const start = i * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const chunk = file.slice(start, end);
        
        const chunkFormData = new FormData();
        chunkFormData.append('chunk', chunk);
        chunkFormData.append('chunkIndex', i);
        chunkFormData.append('totalChunks', totalChunks);
        chunkFormData.append('fileName', file.name);
        
        // For now, simulate upload - in production this would be real chunked upload
        await new Promise(resolve => setTimeout(resolve, 100));
        setUploadProgress(((i + 1) / totalChunks) * 100);
      }

      // Return a simulated URL - in production this would come from upload service
      const fileUrl = `https://storage.example.com/uploads/${Date.now()}-${file.name}`;
      return fileUrl;
    } catch (error) {
      toast.error("Failed to upload file");
      throw error;
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const sendMessageWithMedia = async () => {
    if ((!newMessage.trim() && !selectedFile) || !selectedConversation) return;

    try {
      let messageData = {
        recipient_id: selectedConversation.other_user_id,
        content: newMessage.trim() || (selectedFile ? `Shared ${selectedFile.type.startsWith('image/') ? 'image' : selectedFile.type.startsWith('video/') ? 'video' : 'file'}: ${selectedFile.name}` : ""),
        message_type: "direct"
      };

      // Upload file if selected
      if (selectedFile) {
        const fileUrl = await uploadFile(selectedFile);
        messageData.media_url = fileUrl;
        messageData.media_filename = selectedFile.name;
        messageData.message_type = selectedFile.type.startsWith('image/') ? 'image' : 
                                  selectedFile.type.startsWith('video/') ? 'video' : 'file';
      }

      const token = localStorage.getItem('token');
      await axios.post(`${API}/direct-messages`, messageData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setNewMessage("");
      setSelectedFile(null);
      setFilePreview(null);
      fetchMessages(selectedConversation.other_user_id);
      fetchConversations();
      toast.success("Message sent!");
    } catch (error) {
      toast.error("Failed to send message");
    }
  };

  const removeSelectedFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
  };

  const fetchCoachRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/coach-consultant-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCoachRequests(response.data);
    } catch (error) {
      console.error("Failed to fetch coach requests:", error);
    }
  };

  useEffect(() => {
    fetchCoachRequests();
    fetchUserTeams();
    fetchSchoolMessages();
  }, []);

  const fetchUserTeams = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/teams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserTeams(response.data);
    } catch (error) {
      console.error("Failed to fetch user teams:", error);
    }
  };

  const fetchTeamMessages = async (teamId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/messages/${teamId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Parse timestamp strings back to Date objects
      const parsedMessages = response.data.map(msg => ({
        ...msg,
        timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date()
      }));
      
      setTeamMessages(parsedMessages);
    } catch (error) {
      console.error("Failed to fetch team messages:", error);
    }
  };

  const sendTeamMessage = async () => {
    if ((!newMessage.trim() && !selectedFile) || !selectedTeam) return;

    try {
      let messageData = {
        team_id: selectedTeam.id,
        content: newMessage.trim() || (selectedFile ? `Shared ${selectedFile.type.startsWith('image/') ? 'image' : selectedFile.type.startsWith('video/') ? 'video' : 'file'}: ${selectedFile.name}` : ""),
        message_type: "text"
      };

      // Upload file if selected
      if (selectedFile) {
        const fileUrl = await uploadFile(selectedFile);
        messageData.media_url = fileUrl;
        messageData.media_filename = selectedFile.name;
        messageData.message_type = selectedFile.type.startsWith('image/') ? 'image' : 
                                  selectedFile.type.startsWith('video/') ? 'video' : 'file';
      }

      const token = localStorage.getItem('token');
      await axios.post(`${API}/messages`, messageData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setNewMessage("");
      setSelectedFile(null);
      setFilePreview(null);
      fetchTeamMessages(selectedTeam.id);
      toast.success("Message sent to team!");
    } catch (error) {
      toast.error("Failed to send team message");
    }
  };

  const handleSendMessage = () => {
    if (activeTab === "direct") {
      sendMessageWithMedia();
    } else if (activeTab === "team") {
      sendTeamMessage();
    }
  };

  const fetchSchoolMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/school-messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Parse timestamp strings back to Date objects
      const parsedMessages = response.data.map(msg => ({
        ...msg,
        created_at: msg.created_at ? new Date(msg.created_at) : new Date()
      }));
      
      setSchoolMessages(parsedMessages);
    } catch (error) {
      console.error("Failed to fetch school messages:", error);
    }
  };

  const sendSchoolMessage = async () => {
    if (!schoolMessageData.title.trim() || !schoolMessageData.content.trim()) {
      toast.error("Please fill in both title and content");
      return;
    }

    try {
      setIsSendingSchoolMsg(true);
      let messageData = { ...schoolMessageData };

      // Upload file if selected
      if (selectedFile) {
        const fileUrl = await uploadFile(selectedFile);
        messageData.media_url = fileUrl;
        messageData.media_filename = selectedFile.name;
        messageData.message_type = selectedFile.type.startsWith('image/') ? 'image' : 
                                  selectedFile.type.startsWith('video/') ? 'video' : 'file';
      }

      const token = localStorage.getItem('token');
      await axios.post(`${API}/school-messages`, messageData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Reset form
      setSchoolMessageData({
        title: "",
        content: "",
        priority: "normal"
      });
      setSelectedFile(null);
      setFilePreview(null);
      setShowSchoolMessageForm(false);
      
      fetchSchoolMessages();
      toast.success("School-wide message sent successfully!");
    } catch (error) {
      toast.error("Failed to send school message");
    }
    setIsSendingSchoolMsg(false);
  };

  const canSendSchoolMessages = user && ['school-admin', 'system-admin'].includes(user.role);
  const canViewSchoolMessages = user && ['athlete', 'coach', 'team-admin', 'system-admin', 'school-admin', 'sport-performance-consultant'].includes(user.role);

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-100">
      <Navigation user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-12">
        <div className="mb-4 sm:mb-12">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-2 sm:mb-4">
                Messages
              </h1>
              <p className="text-sm sm:text-lg text-gray-600">
                Connect with coaches and teammates
              </p>
            </div>
            <div className="flex flex-col space-y-3">
              <ConsultantRequestButton user={user} />
              {/* Bulk Message Button - for eligible roles */}
              {user && ['coach', 'team-admin', 'system-admin', 'sport-performance-consultant', 'school-admin'].includes(user.role) && (
                <button
                  onClick={() => setShowBulkComposer(true)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  <span>Send Bulk Message</span>
                </button>
              )}
            </div>
          </div>
          
          {/* Messaging Tabs */}
          <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg mt-6 inline-flex">
            <button
              onClick={() => setActiveTab("direct")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === "direct"
                  ? "bg-white text-sky-700 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Direct Messages
            </button>
            <button
              onClick={() => setActiveTab("team")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === "team"
                  ? "bg-white text-sky-700 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Team Messages ({userTeams.length})
            </button>
            {canViewSchoolMessages && (
              <button
                onClick={() => setActiveTab("school")}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === "school"
                    ? "bg-white text-sky-700 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                School Messages ({schoolMessages.length})
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 h-auto lg:h-[600px]">
          {/* Conversations List */}
          <div className="lg:col-span-1">
            <Card className="h-full">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>
                    {activeTab === "direct" ? "Conversations" : 
                     activeTab === "team" ? "Your Teams" : "School Messages"}
                  </CardTitle>
                  {activeTab === "direct" && (
                    <Button
                      size="sm"
                      onClick={() => setShowUserSearch(true)}
                      className="bg-sky-600 hover:bg-sky-700"
                    >
                      New Chat
                    </Button>
                  )}
                  {activeTab === "school" && canSendSchoolMessages && (
                    <Button
                      size="sm"
                      onClick={() => setShowSchoolMessageForm(true)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      Send School Message
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {/* Coach/Consultant Requests */}
                {coachRequests.length > 0 && (
                  <div className="border-b border-gray-200 bg-yellow-50">
                    <div className="p-4">
                      <h3 className="font-medium text-sm text-yellow-800 mb-3">Coach/Consultant Requests</h3>
                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        {coachRequests.slice(0, 3).map((request) => (
                          <div key={request.id} className="bg-white p-2 rounded border border-yellow-200">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <p className="text-xs font-medium text-gray-900 capitalize">
                                  {request.request_type.replace('-', ' ')} Request
                                </p>
                                <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                                  {request.message}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  From: {request.from_user_name}
                                </p>
                              </div>
                              <span className={`text-xs px-2 py-1 rounded ${
                                request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                request.status === 'assigned' ? 'bg-blue-100 text-blue-800' :
                                'bg-green-100 text-green-800'
                              }`}>
                                {request.status}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                      {coachRequests.length > 3 && (
                        <p className="text-xs text-yellow-700 mt-2">
                          +{coachRequests.length - 3} more requests
                        </p>
                      )}
                    </div>
                  </div>
                )}
                
                <div className="max-h-[500px] overflow-y-auto">
                  {activeTab === "school" && showSchoolMessageForm ? (
                    // School Message Form
                    <div className="p-4 bg-green-50 border-b">
                      <h3 className="font-medium text-green-800 mb-3">Send School-Wide Message</h3>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                          <input
                            type="text"
                            value={schoolMessageData.title}
                            onChange={(e) => setSchoolMessageData(prev => ({...prev, title: e.target.value}))}
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                            placeholder="Message title..."
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                          <textarea
                            value={schoolMessageData.content}
                            onChange={(e) => setSchoolMessageData(prev => ({...prev, content: e.target.value}))}
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm h-20"
                            placeholder="Message content..."
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                          <select
                            value={schoolMessageData.priority}
                            onChange={(e) => setSchoolMessageData(prev => ({...prev, priority: e.target.value}))}
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                          >
                            <option value="normal">Normal</option>
                            <option value="high">High Priority</option>
                            <option value="urgent">Urgent</option>
                          </select>
                        </div>
                        <div className="flex space-x-2">
                          <Button
                            size="sm"
                            onClick={sendSchoolMessage}
                            disabled={isSendingSchoolMsg}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            {isSendingSchoolMsg ? "Sending..." : "Send to School"}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setShowSchoolMessageForm(false)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : activeTab === "direct" && showUserSearch ? (
                    <div className="p-4">
                      <div className="flex items-center space-x-2 mb-4">
                        <Search className="w-4 h-4 text-gray-400" />
                        <Input
                          placeholder="Search users..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="flex-1"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setShowUserSearch(false)}
                        >
                          Cancel
                        </Button>
                      </div>
                      <div className="space-y-2">
                        {filteredUsers.slice(0, 10).map((user) => (
                          <div
                            key={user.id}
                            onClick={() => startNewConversation(user)}
                            className="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer"
                          >
                            <User className="w-8 h-8 text-gray-400" />
                            <div>
                              <p className="font-medium">{user.name}</p>
                              <p className="text-sm text-gray-500 capitalize">{user.role}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : activeTab === "school" ? (
                    // School Messages List
                    schoolMessages.map((message) => (
                      <div
                        key={message.id}
                        className={`p-4 hover:bg-gray-50 border-b ${
                          message.priority === 'urgent' ? 'border-l-4 border-red-500 bg-red-50' :
                          message.priority === 'high' ? 'border-l-4 border-yellow-500 bg-yellow-50' : ''
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-1">
                              <p className="font-medium text-gray-900 truncate">{message.title}</p>
                              {message.priority !== 'normal' && (
                                <span className={`text-xs px-2 py-1 rounded ${
                                  message.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                                  'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {message.priority.toUpperCase()}
                                </span>
                              )}
                            </div>
                            <LinkifyText className="text-sm text-gray-600 line-clamp-2 mb-2">
                              {message.content}
                            </LinkifyText>
                            <div className="flex items-center justify-between">
                              <p className="text-xs text-gray-500">From: {message.sender_name}</p>
                              <p className="text-xs text-gray-500">
                                {message.created_at.toLocaleDateString()} {message.created_at.toLocaleTimeString()}
                              </p>
                            </div>
                            {message.media_url && (
                              <div className="mt-2">
                                <span className="text-xs text-blue-600">📎 {message.media_filename}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : activeTab === "direct" ? (
                    conversations.map((conv) => (
                      <div
                        key={`${conv.other_user_id}-${conv.last_message_time}`}
                        onClick={() => setSelectedConversation(conv)}
                        className={`flex items-center space-x-3 p-4 hover:bg-gray-50 cursor-pointer border-b ${
                          selectedConversation?.other_user_id === conv.other_user_id ? 'bg-sky-50' : ''
                        }`}
                      >
                        <User className="w-10 h-10 text-gray-400" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{conv.other_user_name}</p>
                          <p className="text-sm text-gray-500 truncate">{conv.last_message_preview || 'No messages yet'}</p>
                          <p className="text-xs text-gray-400 capitalize">{conv.other_user_role}</p>
                        </div>
                      </div>
                    ))
                  ) : activeTab === "team" ? (
                    // Team Messages List
                    userTeams.map((team) => (
                      <div
                        key={team.id}
                        onClick={() => {
                          setSelectedTeam(team);
                          setSelectedConversation(null);
                          fetchTeamMessages(team.id);
                        }}
                        className={`flex items-center space-x-3 p-4 hover:bg-gray-50 cursor-pointer border-b ${
                          selectedTeam?.id === team.id ? 'bg-sky-50' : ''
                        }`}
                      >
                        <Users className="w-10 h-10 text-gray-400" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{team.name}</p>
                          <p className="text-sm text-gray-500 truncate">{team.description || 'Team chat'}</p>
                          <p className="text-xs text-gray-400">
                            {team.athlete_ids?.length || 0} members
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    // Empty state for school messages
                    <div className="text-center p-8 text-gray-500">
                      <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No school messages yet</p>
                      {canSendSchoolMessages && (
                        <p className="text-sm mt-1">Click &ldquo;Send School Message&rdquo; to communicate with all coaches and athletes</p>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Messages Area */}
          <div className="lg:col-span-2">
            <Card className="h-full flex flex-col">
              {selectedConversation || selectedTeam ? (
                <>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-3">
                      {selectedConversation ? (
                        <>
                          <User className="w-6 h-6 text-gray-400" />
                          <div>
                            <span>{selectedConversation.other_user_name}</span>
                            <p className="text-sm text-gray-500 font-normal capitalize">
                              {selectedConversation.other_user_role}
                            </p>
                          </div>
                        </>
                      ) : (
                        <>
                          <Users className="w-6 h-6 text-blue-400" />
                          <div>
                            <span>{selectedTeam.name}</span>
                            <p className="text-sm text-gray-500 font-normal">
                              Team Chat • {selectedTeam.athlete_ids?.length || 0} members
                            </p>
                          </div>
                        </>
                      )}
                    </CardTitle>
                  </CardHeader>
                  
                  <CardContent className="flex-1 flex flex-col p-4">
                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto mb-4 space-y-3 max-h-[400px]">
                      {(selectedConversation ? messages : teamMessages).map((message) => (
                        <div
                          key={message.id}
                          className={`flex ${(message.sender_id || message.user_id) === user.id ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[70%] px-4 py-2 rounded-lg ${
                              (message.sender_id || message.user_id) === user.id
                                ? 'bg-sky-600 text-white'
                                : 'bg-gray-200 text-gray-900'
                            }`}
                          >
                            {/* Media Content */}
                            {message.message_type === 'image' && message.media_url && (
                              <div className="mb-2">
                                <div className="relative">
                                  <img 
                                    src={message.media_url} 
                                    alt={message.media_filename || "Shared image"}
                                    className="max-w-full h-auto rounded-md cursor-pointer"
                                    onClick={() => window.open(message.media_url, '_blank')}
                                  />
                                  <div className="absolute top-2 right-2">
                                    <MessageAttachmentFavoriteButton 
                                      messageId={message.id}
                                      filename={message.media_filename}
                                      attachmentType="image"
                                      mediaUrl={message.media_url}
                                    />
                                  </div>
                                </div>
                                <p className="text-xs mt-1 opacity-75">{message.media_filename}</p>
                              </div>
                            )}
                            
                            {message.message_type === 'video' && message.media_url && (
                              <div className="mb-2">
                                <div className="relative">
                                  <video 
                                    src={message.media_url} 
                                    controls 
                                    className="max-w-full h-auto rounded-md"
                                    style={{ maxHeight: '200px' }}
                                  >
                                    Your browser does not support the video tag.
                                  </video>
                                  <div className="absolute top-2 right-2">
                                    <MessageAttachmentFavoriteButton 
                                      messageId={message.id}
                                      filename={message.media_filename}
                                      attachmentType="video"
                                      mediaUrl={message.media_url}
                                    />
                                  </div>
                                </div>
                                <p className="text-xs mt-1 opacity-75">{message.media_filename}</p>
                              </div>
                            )}
                            
                            {message.message_type === 'file' && message.media_url && (
                              <div className="mb-2">
                                <div className="flex items-center justify-between">
                                  <a 
                                    href={message.media_url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className={`flex items-center space-x-2 p-2 rounded border flex-grow ${
                                      message.sender_id === user.id 
                                        ? 'border-sky-300 hover:bg-sky-500' 
                                        : 'border-gray-300 hover:bg-gray-100'
                                    }`}
                                  >
                                    <File className="w-4 h-4" />
                                    <span className="text-sm">{message.media_filename || 'Download file'}</span>
                                  </a>
                                  <div className="ml-2">
                                    <MessageAttachmentFavoriteButton 
                                      messageId={message.id}
                                      filename={message.media_filename}
                                      attachmentType="document"
                                      mediaUrl={message.media_url}
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                            
                            {/* Text Content */}
                            {message.content && (
                              <LinkifyText>
                                {message.content}
                              </LinkifyText>
                            )}
                            
                            <p className={`text-xs mt-1 ${
                              (message.sender_id || message.user_id) === user.id ? 'text-sky-100' : 'text-gray-500'
                            }`}>
                              {selectedConversation ? (
                                new Date(message.sent_at).toLocaleTimeString()
                              ) : (
                                <>
                                  <span className="font-medium">{message.user_name}: </span>
                                  {new Date(message.timestamp).toLocaleTimeString()}
                                </>
                              )}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Media Preview */}
                    {selectedFile && (
                      <div className="mb-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                        <div className="flex items-start space-x-3">
                          <div className="flex-1">
                            {filePreview && selectedFile.type.startsWith('image/') ? (
                              <img 
                                src={filePreview} 
                                alt="Preview" 
                                className="max-w-48 max-h-32 rounded object-cover"
                              />
                            ) : (
                              <div className="flex items-center space-x-2">
                                <File className="w-8 h-8 text-blue-500" />
                                <div>
                                  <p className="font-medium text-sm">{selectedFile.name}</p>
                                  <p className="text-xs text-gray-500">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                                </div>
                              </div>
                            )}
                            {isUploading && (
                              <div className="mt-2">
                                <div className="flex justify-between text-xs text-gray-600 mb-1">
                                  <span>Uploading...</span>
                                  <span>{uploadProgress.toFixed(0)}%</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                  <div 
                                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${uploadProgress}%` }}
                                  ></div>
                                </div>
                              </div>
                            )}
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={removeSelectedFile}
                            disabled={isUploading}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Message Input */}
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <div className="relative">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setShowMediaMenu(!showMediaMenu)}
                            disabled={isUploading}
                          >
                            <Paperclip className="w-4 h-4" />
                          </Button>
                          
                          {showMediaMenu && (
                            <div className="absolute bottom-full left-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                              <div className="p-2 space-y-1">
                                <label className="flex items-center space-x-2 px-3 py-2 hover:bg-gray-100 rounded cursor-pointer">
                                  <Image className="w-4 h-4 text-green-600" />
                                  <span className="text-sm">Photo</span>
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileSelect}
                                    className="hidden"
                                  />
                                </label>
                                <label className="flex items-center space-x-2 px-3 py-2 hover:bg-gray-100 rounded cursor-pointer">
                                  <Video className="w-4 h-4 text-purple-600" />
                                  <span className="text-sm">Video</span>
                                  <input
                                    type="file"
                                    accept="video/*"
                                    onChange={handleFileSelect}
                                    className="hidden"
                                  />
                                </label>
                                <label className="flex items-center space-x-2 px-3 py-2 hover:bg-gray-100 rounded cursor-pointer">
                                  <File className="w-4 h-4 text-blue-600" />
                                  <span className="text-sm">File</span>
                                  <input
                                    type="file"
                                    accept=".pdf,.doc,.docx,.txt"
                                    onChange={handleFileSelect}
                                    className="hidden"
                                  />
                                </label>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <Input
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          placeholder="Type your message..."
                          onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                          className="flex-1"
                        />
                        <Button
                          onClick={handleSendMessage}
                          disabled={(!newMessage.trim() && !selectedFile) || isUploading}
                          className="bg-sky-600 hover:bg-sky-700"
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </>
              ) : (
                <CardContent className="flex-1 flex items-center justify-center">
                  <div className="text-center text-gray-500">
                    {activeTab === "direct" ? (
                      <>
                        <MessageSquare className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                        <p className="text-lg">Select a conversation to start messaging</p>
                        <p className="text-sm">or click &ldquo;New Chat&rdquo; to start a conversation</p>
                      </>
                    ) : activeTab === "team" ? (
                      <>
                        <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                        <p className="text-lg">Select a team to view team messages</p>
                        <p className="text-sm">Connect with your team members in group chats</p>
                      </>
                    ) : (
                      <>
                        <School className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                        <p className="text-lg">School-wide messages appear here</p>
                        <p className="text-sm">Messages from school administrators to all coaches and athletes</p>
                      </>
                    )}
                  </div>
                </CardContent>
              )}
            </Card>
          </div>
        </div>
      </div>
      
      {/* Bulk Message Composer Modal */}
      {showBulkComposer && (
        <BulkMessageComposer
          user={user}
          onClose={() => setShowBulkComposer(false)}
        />
      )}
    </div>
  );
}